import { useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../design-v2";
import AuthLayout from "../AuthLayout";
import ProviderButton, {
  readLastUsedAuthProvider,
} from "../components/ProviderButton";
import { getAuthenticatedDestination } from "../authRouting";
import {
  buildAuthEmailRecoveryPath,
  readValidatedNext,
} from "../returnPath";
import useAuthBootstrap from "../useAuthBootstrap";
import { authErrorCopy } from "./AccountGateScreen";
import { resolveAnonymousEntry } from "../../features/onboarding/anonymousEntry";
import { loadAnonymousOnboardingDraft } from "../../features/onboarding/onboardingDraftStore";
import { getStartableAuthProviders } from "../providerCapabilities";
import { startProviderAuthentication } from "../oauthStart";

export default function WelcomeBackScreen() {
  const bootstrap = useAuthBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const next = readValidatedNext(location.search);
  const [pendingProvider, setPendingProvider] = useState(null);
  const lastUsedProvider = useMemo(() => readLastUsedAuthProvider(), []);
  const startableProviders = getStartableAuthProviders(bootstrap.providers);
  const error = authErrorCopy(location.search);

  if (bootstrap.status === "authenticated") {
    return (
      <Navigate
        to={getAuthenticatedDestination(bootstrap.onboarding, next)}
        replace
      />
    );
  }

  const selectProvider = (providerId) => {
    setPendingProvider(providerId);
    if (providerId === "email") {
      navigate(buildAuthEmailRecoveryPath(next || "/"), {
        state: { authSurface: "/login" },
      });
      return;
    }
    // The deep-link destination rides through the OAuth round trip in the
    // signed state cookie and comes back out at /auth/return.
    if (!startProviderAuthentication({
      providerId,
      returnTo: next || "/",
      surface: "/login",
    })) {
      setPendingProvider(null);
    }
  };

  return (
    <AuthLayout>
      <section className="lz-auth-provider-screen" aria-labelledby="welcome-back-title">
        <div className="lz-auth-provider-screen__intro lz-auth-intro">
          <h1 id="welcome-back-title">
            WELCOME<br /><span>BACK.</span>
          </h1>
          <p>Log in to continue your training.</p>
        </div>

        <div className="lz-auth-provider-screen__actions">
          {startableProviders.map((providerId) => (
            <ProviderButton
              key={providerId}
              providerId={providerId}
              isLastUsed={providerId === lastUsedProvider}
              isLoading={pendingProvider === providerId}
              disabled={Boolean(pendingProvider)}
              onClick={() => selectProvider(providerId)}
            />
          ))}
        </div>

        {error ? <p className="lz-auth-provider-screen__error" role="alert">{error}</p> : null}

        <p className="lz-auth-provider-screen__secondary">
          New to Lyft Zone?{" "}
          <Button
            variant="ghost"
            onClick={() => navigate(resolveAnonymousEntry({
              draft: loadAnonymousOnboardingDraft(),
            }).destination)}
          >
            Get started
          </Button>
        </p>
      </section>
    </AuthLayout>
  );
}
